import {
  StudyThreadCreateDtoKillChainCategoryEnum,
  StudyThreadRole,
  type StudyThreadSelectDto,
} from '@@types/api.types';
import { Modal } from '@components/core/modal.component';
import { ConfirmDeleteModal } from '@components/shared/confirm-delete-modal.component';
import { Button } from '@components/ui/button';
import { useAppForm } from '@components/ui/form/form';
import { errorToast, successToast } from '@hooks/toast.hook';
import {
  useCreateMeiaMissionThread,
  useDeleteMeiaMissionThread,
  useUpdateMeiaMissionThread,
} from '@queries/meia.query';
import { type MeiaThreadFormValues, meiaThreadSchema } from '@schemas/meia.schema';
import { createFormInvalidHandler } from '@utils/form.util';
import { toCreateThreadKillChain, toUpdateThreadKillChain } from '@utils/meia-enum.util';
import { Trash2Icon } from 'lucide-react';
import { type FC, useState } from 'react';
import { MEIA_AUTOFILL_IGNORE_PROPS } from './meia-form-autofill.util';

type MeiaThreadModalProps = {
  isOpen: boolean;
  missionId: string;
  onClose: () => void;
  studyId: string;
  thread?: StudyThreadSelectDto;
};

const KILL_CHAIN_OPTIONS: ReadonlyArray<{
  label: string;
  value: StudyThreadCreateDtoKillChainCategoryEnum;
}> = [
  {
    label: 'F2T2EA',
    value: StudyThreadCreateDtoKillChainCategoryEnum.F2T2EA,
  },
  {
    label: 'D3A',
    value: StudyThreadCreateDtoKillChainCategoryEnum.D3A,
  },
  {
    label: 'Cyber',
    value: StudyThreadCreateDtoKillChainCategoryEnum.CYBER,
  },
  {
    label: 'ISR',
    value: StudyThreadCreateDtoKillChainCategoryEnum.ISR,
  },
  {
    label: 'Logistics',
    value: StudyThreadCreateDtoKillChainCategoryEnum.LOGISTICS,
  },
  { label: 'C2', value: StudyThreadCreateDtoKillChainCategoryEnum.C2 },
  {
    label: 'cUAS',
    value: StudyThreadCreateDtoKillChainCategoryEnum.COUNTER_UAS,
  },
  {
    label: 'Custom',
    value: StudyThreadCreateDtoKillChainCategoryEnum.CUSTOM,
  },
];

const ROLE_OPTIONS: ReadonlyArray<{ label: string; value: StudyThreadRole }> = [
  { label: 'Primary', value: StudyThreadRole.PRIMARY },
  { label: 'Supporting', value: StudyThreadRole.SUPPORTING },
  { label: 'Shaping', value: StudyThreadRole.SHAPING },
];

export const MeiaThreadModal: FC<MeiaThreadModalProps> = (props) => {
  const { isOpen, missionId, onClose, studyId, thread } = props;
  const isEdit = Boolean(thread);
  const [isConfirmingDelete, setIsConfirmingDelete] = useState(false);

  const { isPending: isCreating, mutateAsync: createThread } = useCreateMeiaMissionThread();
  const { isPending: isUpdating, mutateAsync: updateThread } = useUpdateMeiaMissionThread();
  const { mutateAsync: deleteThread } = useDeleteMeiaMissionThread();

  const defaultValues: MeiaThreadFormValues = {
    killChainCategory: thread
      ? toCreateThreadKillChain(thread.killChainCategory)
      : StudyThreadCreateDtoKillChainCategoryEnum.F2T2EA,
    name: thread?.name ?? '',
    narrative: thread?.narrative ?? '',
    role: thread?.role ?? StudyThreadRole.PRIMARY,
  };

  const form = useAppForm({
    defaultValues,
    onSubmit: async ({ value }) => {
      const validation = meiaThreadSchema.safeParse(value);
      if (!validation.success) {
        errorToast({ description: 'Please complete every required field.' });
        return;
      }

      const payload = validation.data;
      if (thread) {
        await updateThread({
          killChainCategory: toUpdateThreadKillChain(payload.killChainCategory),
          missionId,
          name: payload.name,
          narrative: payload.narrative,
          role: payload.role,
          studyId,
          threadId: thread.id,
        });
        successToast({ description: 'Thread updated.' });
      } else {
        await createThread({
          killChainCategory: payload.killChainCategory,
          missionId,
          name: payload.name,
          narrative: payload.narrative,
          position: 0,
          role: payload.role,
          studyId,
        });
        successToast({ description: 'Mission Thread created.' });
      }
      onClose();
    },
    onSubmitInvalid: createFormInvalidHandler<typeof defaultValues>(),
    validators: { onSubmit: meiaThreadSchema },
  });

  const handleConfirmDelete = async (): Promise<void> => {
    if (!thread) return;
    await deleteThread({ missionId, studyId, threadId: thread.id });
    successToast({ description: 'Thread deleted.' });
    onClose();
  };

  const isSaving = isCreating || isUpdating;

  return (
    <>
      <Modal
        centered
        footer={{
          leftButton: isEdit ? (
            <Button
              onClick={() => setIsConfirmingDelete(true)}
              type="button"
              variant="destructive"
            >
              <Trash2Icon className="size-4" />
              Delete
            </Button>
          ) : undefined,
          rightButton: {
            children: isSaving ? 'Saving...' : isEdit ? 'Save' : 'Add Thread',
            disabled: isSaving,
            onClick: () => void form.handleSubmit(),
          },
        }}
        header={{ title: isEdit ? 'Edit Thread' : 'Add Thread' }}
        isOpen={isOpen}
        onClose={onClose}
        width="640px"
      >
        <form
          {...MEIA_AUTOFILL_IGNORE_PROPS}
          className="flex flex-col gap-4"
          onSubmit={(event) => {
            event.preventDefault();
            event.stopPropagation();
            void form.handleSubmit();
          }}
        >
          <form.AppForm>
            <form.AppField name="name">
              {(field) => (
                <field.TextField
                  {...MEIA_AUTOFILL_IGNORE_PROPS}
                  label="Name"
                  placeholder="e.g., Dynamic strike on time-sensitive targets"
                  required
                />
              )}
            </form.AppField>
            <form.AppField name="killChainCategory">
              {(field) => (
                <field.SelectField
                  label="Pattern"
                  options={KILL_CHAIN_OPTIONS.map((option) => ({
                    label: option.label,
                    value: option.value,
                  }))}
                  required
                />
              )}
            </form.AppField>
            <form.AppField name="role">
              {(field) => (
                <field.SelectField
                  label="Role"
                  options={ROLE_OPTIONS.map((option) => ({ label: option.label, value: option.value }))}
                  required
                />
              )}
            </form.AppField>
            <form.AppField name="narrative">
              {(field) => (
                <field.TextAreaField
                  label="Narrative"
                  placeholder="Describe what this Thread accomplishes within the Mission."
                  required
                  rows={5}
                />
              )}
            </form.AppField>
          </form.AppForm>
        </form>
      </Modal>
      {thread && (
        <ConfirmDeleteModal
          isOpen={isConfirmingDelete}
          itemName="thread"
          onConfirm={handleConfirmDelete}
          setIsOpen={setIsConfirmingDelete}
        />
      )}
    </>
  );
};
